import React, { useState, useEffect } from "react";
import Modal from "../../UI/Modal";
import QRCode from "qrcode.react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import {
  Document,
  Packer,
  Paragraph,
  Table,
  TableRow,
  TableCell,
  ImageRun,
  TextRun,
  HeadingLevel,
  AlignmentType,
  WidthType,
  PageBreak,
  convertMillimetersToTwip,
  TableLayoutType,
} from "docx";
import { saveAs } from "file-saver";
import {
  Box,
  Typography,
  Button,
  TextField,
  Divider,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
} from "@mui/material";
import { green, grey, blue } from "@mui/material/colors";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddBoxIcon from "@mui/icons-material/AddBox";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CloseIcon from "@mui/icons-material/Close";
import { LightTooltip } from "@mui/material";

const statusTitle = {
  fontWeight: "bold",
  backgroundColor: green[900],
  color: "white",
};

const mgrbox = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  backgroundColor: green[50],
  padding: "20px",
  minHeight: "400px",
};

const inputBox = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  marginBottom: "20px",
  width: "100%",
  justifyContent: "center",
};

const generateBtn = {
  fontWeight: "bold",
  backgroundColor: green[600],
  color: "white",
  height: "40px",
  "&:hover": {
    backgroundColor: green[800],
  },
};

const downloadBtn = {
  fontWeight: "bold",
  backgroundColor: green[500],
  color: "white",
  marginTop: "2rem",
  padding: "10px 20px",
  "&:hover": {
    backgroundColor: green[700],
  },
};

const qrContainer = {
  width: "100%",
  maxHeight: "500px",
  overflowY: "auto",
  padding: "20px",
  backgroundColor: "white",
  borderRadius: "4px",
  marginTop: "20px",
};

const qrItem = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "10px",
  border: `1px solid ${grey[300]}`,
  borderRadius: "4px",
  backgroundColor: "white",
};

const buttonGroup = {
  display: "flex",
  gap: "10px",
};

const downloadAllBtn = {
  fontWeight: "bold",
  backgroundColor: blue[600],
  color: "white",
  height: "40px",
  "&:hover": {
    backgroundColor: blue[800],
  },
};

const LABEL_FORMATS = {
  "LS-3105": {
    name: "폼텍 21칸 (7X3) LS-3105",
    rows: 7,
    cols: 3,
    width: 65,
    height: 38.1,
    qrSize: 25,
    marginTop: 15.15,
    marginLeft: 5.15,
  },
  "LS-3107": {
    name: "폼텍 16칸 (8X2) LS-3107",
    rows: 8,
    cols: 2,
    width: 99.1, // mm
    height: 33.9, // mm
    qrSize: 25, // QR 코드 크기 (mm)
    marginTop: 21.2, // mm
    marginLeft: 4.65, // mm
    description: "레이저/잉크젯 겸용 | 100매 | 흰색 | A4",
  },
  "LS-3106": {
    name: "폼텍 24칸 (6X4) LS-3106",
    rows: 6,
    cols: 4,
    width: 64, // mm
    height: 34, // mm
    qrSize: 20, // QR 코드 크기 (mm)
    marginTop: 10, // mm
    marginLeft: 5, // mm
    description: "레이저/잉크젯 겸용 | 100매 | 흰색 | A4",
  },
  "LS-3109": {
    name: "폼텍 18칸 (2X9) LS-3109",
    rows: 9,
    cols: 2,
    width: 100, // mm
    height: 30, // mm
    qrSize: 22, // QR 코드 크기 (mm)
    marginTop: 9, // mm
    marginLeft: 4, // mm
    description: "레이저/잉크젯 겸용 | 100매 | 흰색 | A4",
  },
  "LS-3108": {
    name: "폼텍 14칸 (2X7) LS-3108",
    rows: 7,
    cols: 2,
    width: 99.1, // mm
    height: 38.1, // mm
    qrSize: 30, // QR 코드 크기 (mm)
    marginTop: 15, // mm
    marginLeft: 5, // mm
    description: "레이저/잉크젯 겸용 | 100매 | 흰색 | A4",
  },
  "LS-3212": {
    name: "폼텍 22칸 (11X2) LS-3212",
    rows: 11,
    cols: 2,
    width: 100, // mm
    height: 24.5, // mm
    qrSize: 20, // QR 코드 크기 (mm)
    marginTop: 5, // mm
    marginLeft: 5, // mm
    description: "레이저/잉크젯 겸용 | 100매 | 흰색 | A4",
  },
  "LS-3218": {
    name: "폼텍 18칸 (6X3) LS-3218",
    rows: 6,
    cols: 3,
    width: 63.5, // mm
    height: 45, // mm
    qrSize: 20, // QR 코드 크기 (mm)
    marginTop: 10, // mm
    marginLeft: 5, // mm
    description: "레이저/잉크젯 겸용 | 100매 | 흰색 | A4",
  },
};

const previewStyles = {
  previewContainer: {
    width: "100%",
    height: "calc(100vh - 350px)", // 상단 여백과 입력 영역을 고려한 높이 조정
    padding: "20px",
    backgroundColor: grey[100],
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: grey[200],
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: grey[400],
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: grey[500],
    },
  },
  qrGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
    gap: "20px",
    width: "100%",
  },
  qrCell: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "15px",
    backgroundColor: "white",
    borderRadius: "8px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    transition: "transform 0.2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
    },
  },
  qrValue: {
    fontSize: "12px",
    marginTop: "10px",
    marginBottom: "10px",
    textAlign: "center",
    color: grey[600],
  },
  downloadButton: {
    minWidth: "unset",
    padding: "4px 8px",
    fontSize: "0.75rem",
    backgroundColor: green[600],
    color: "white",
    "&:hover": {
      backgroundColor: green[800],
    },
  },
};

const modalStyles = {
  height: "calc(100vh - 200px)", // 모달의 전체 높이를 동원자원관리와 동일하게 설정
  display: "flex",
  flexDirection: "column",
};

const contentStyles = {
  flex: 1, // 남은 공간을 모두 차지
  overflowY: "hidden", // 컨텐츠 영역 스크롤 방지
  display: "flex",
  flexDirection: "column",
};

const QRmanager = (props) => {
  const [title, setTitle] = useState("강원소방");
  const [quantity, setQuantity] = useState(21);
  const [qrCodes, setQrCodes] = useState([]);
  const [labelFormat, setLabelFormat] = useState("LS-3105");
  const [showHelp, setShowHelp] = useState(false);

  // 현재 시간 + 랜덤값으로 고유한 일련번호 생성 함수 수정
  const generateSerialNumber = () => {
    // 현재 시간을 밀리초 단위로 가져옵니다.
    const timestamp = Date.now();
    // 랜덤값 생성 (0-999)
    const random = Math.floor(Math.random() * 1000)
      .toString()
      .padStart(3, "0");
    return `${timestamp}${random}`;
  };

  // 라벨 형식 변경 핸들러
  const handleFormatChange = (e) => {
    console.log(e.target.value);
    setLabelFormat(e.target.value);
    // QR 코드가 이미 생성되어 있다면 재배열
    if (qrCodes.length > 0) {
      generateQRCodes();
    }
  };

  // QR 코드 생성 함수 수정
  const generateQRCodes = () => {
    if (!title.trim()) {
      alert("제목을 입력해주세요.");
      return;
    }

    const format = LABEL_FORMATS[labelFormat];
    console.log(format);
    const itemsPerPage = format.rows * format.cols;
    const newCodes = [];

    // 기존 QR 코드 유지하면서 새로운 코드 추가
    const existingCount = qrCodes.length;
    console.log(existingCount);
    const targetCount = Math.max(quantity, existingCount);

    for (let i = 0; i < targetCount; i++) {
      if (i < existingCount) {
        // 기존 QR 코드 유지
        newCodes.push(qrCodes[i]);
      } else {
        // 새로운 QR 코드 생성
        const serialNumber = generateSerialNumber();
        newCodes.push({
          id: i,
          title,
          serialNumber,
          value: `${title}_${serialNumber}`,
        });
      }
    }

    setQrCodes(newCodes);
  };

  // QR 코드 정보 표시 포맷
  const formatQRInfo = (qrCode) => {
    const [timestamp, random] = qrCode.serialNumber.split("-");
    return `${qrCode.title} #${random}`;
  };

  // 단일 QR 코드 다운로드
  const downloadSingleQR = (qrCode) => {
    const canvas = document.getElementById(`qr-code-${qrCode.id}`);
    const pngUrl = canvas
      .toDataURL("image/png")
      .replace("image/png", "image/octet-stream");

    const downloadLink = document.createElement("a");
    downloadLink.href = pngUrl;
    downloadLink.download = `QR_${qrCode.title}_${qrCode.serialNumber}.png`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  // QR 코드 이미지를 base64로 변환
  const getQRCodeImage = (qrCode) => {
    return new Promise((resolve, reject) => {
      const canvas = document.getElementById(`qr-code-${qrCode.id}`);
      if (!canvas) {
        reject(new Error(`QR code canvas not found for id: ${qrCode.id}`));
        return;
      }
      resolve(canvas.toDataURL("image/png"));
    });
  };

  // saveAsPDF 함수를 printQRCodes 함수로 변경
  const printQRCodes = () => {
    if (qrCodes.length === 0) {
      alert("생성된 QR코드가 없습니다.");
      return;
    }

    const format = LABEL_FORMATS[labelFormat];
    const itemsPerPage = format.rows * format.cols;
    const totalPages = Math.ceil(qrCodes.length / itemsPerPage);

    try {
      // 인쇄용 스타일 생성
      const style = document.createElement("style");
      style.textContent = `
        @page {
          size: A4 portrait;
          margin: 0;
        }
        @media print {
          body * {
            visibility: hidden;
          }
          #print-container, #print-container * {
            visibility: visible;
          }
          #print-container {
            position: absolute;
            left: 0;
            top: 0;
            width: 210mm;
            height: 297mm;
          }
          .qr-grid {
            display: grid;
            grid-template-columns: repeat(${format.cols}, 1fr);
            grid-template-rows: repeat(${format.rows}, 1fr);
            gap: 0;
            width: 100%;
            padding: ${format.marginTop}mm ${format.marginLeft}mm;
            page-break-after: always; /* 페이지 구분 */
            box-sizing: border-box;
          }
          .qr-grid:last-child {
            page-break-after: auto; /* 마지막 그리드는 페이지 나눔 제거 */
          }
          .qr-cell {
            width: ${format.width}mm;
            height: ${format.height}mm;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            border: 0.5pt solid #000;
            box-sizing: border-box;
            padding: 2mm;
            margin: 0;
          }
          .qr-cell img {
            width: ${format.qrSize}mm;
            height: ${format.qrSize}mm;
            margin-bottom: 2mm;
          }
          .qr-cell p {
            margin: 0;
            font-size: 8pt;
            text-align: center;
            word-break: break-all;
          }
        }
      `;
      document.head.appendChild(style);

      // 인쇄 컨테이너 생성
      const printContainer = document.createElement("div");
      printContainer.id = "print-container";
      document.body.appendChild(printContainer);

      // 페이지별로 QR코드 그리드 생성
      for (let page = 0; page < totalPages; page++) {
        const startIndex = page * itemsPerPage;
        const endIndex = Math.min((page + 1) * itemsPerPage, qrCodes.length);
        const pageQRCodes = qrCodes.slice(startIndex, endIndex);

        // 각 페이지마다 새로운 그리드 생성
        const pageGrid = document.createElement("div");
        pageGrid.className = "qr-grid";

        // QR 코드 배치
        pageQRCodes.forEach((qrCode) => {
          const cell = document.createElement("div");
          cell.className = "qr-cell";

          // QR 코드 이미지 복제
          const canvas = document.getElementById(`qr-code-${qrCode.id}`);
          const img = new Image();
          img.src = canvas.toDataURL("image/png");

          // 텍스트 추가
          const p = document.createElement("p");
          p.textContent = qrCode.value;

          cell.appendChild(img);
          cell.appendChild(p);
          pageGrid.appendChild(cell);
        });

        printContainer.appendChild(pageGrid);
      }

      // 인쇄 실행
      setTimeout(() => {
        window.print();
        // 인쇄 후 정리
        document.head.removeChild(style);
        document.body.removeChild(printContainer);
      }, 500);
    } catch (error) {
      console.error("인쇄 중 오류:", error);
      alert("인쇄 중 오류가 발생했습니다.");
    }
  };

  // QR 코드 미리보기 렌더링
  const renderQRPreview = () => {
    return (
      <Box sx={previewStyles.previewContainer}>
        <Box sx={previewStyles.qrGrid}>
          {qrCodes.map((qrCode) => (
            <Box key={qrCode.id} sx={previewStyles.qrCell}>
              <QRCode
                id={`qr-code-${qrCode.id}`}
                value={qrCode.value}
                size={120}
                level={"H"}
                includeMargin={false}
              />
              <Typography sx={previewStyles.qrValue}>{qrCode.value}</Typography>
              <Button
                variant="contained"
                size="small"
                sx={previewStyles.downloadButton}
                onClick={() => downloadSingleQR(qrCode)}
                startIcon={<FileDownloadIcon />}
              >
                다운로드
              </Button>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  // 닫기 버튼 스타일 추가
  const closeBtn = {
    color: "yellow",
    cursor: "pointer",
    "&:hover": {
      color: "white",
    },
  };

  // QR 코드 초기화 함수 추가
  const resetQRCodes = () => {
    setQrCodes([]);
  };

  return (
    <>
    {showHelp && (
      <div
        onClick={() => setShowHelp(false)}
        style={{
          position: "fixed", inset: 0,
          backgroundColor: "rgba(0,0,0,0.55)",
          zIndex: 9999,
          display: "flex", alignItems: "flex-start", justifyContent: "center",
          overflowY: "auto",
          padding: "65px 0 40px",
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            backgroundColor: "#fff",
            borderRadius: 10,
            padding: "28px 32px",
            maxWidth: 500,
            width: "90%",
            boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
            position: "relative",
          }}
        >
          <button
            onClick={() => setShowHelp(false)}
            style={{
              position: "absolute", top: 12, right: 14,
              background: "none", border: "none",
              fontSize: "1.3rem", cursor: "pointer", color: "#888",
            }}
          >✕</button>

          <h3 style={{ margin: "0 0 16px", color: "#2e7d32", fontSize: "1.05rem", borderBottom: "2px solid #a5d6a7", paddingBottom: 8 }}>
            📋 QR코드 관리 기본 사용법
          </h3>
          <ol style={{ margin: 0, paddingLeft: 20, lineHeight: 2, fontSize: "0.9rem", color: "#333" }}>
            <li><b>제목</b>을 입력합니다 <span style={{ color: "#888" }}>(인쇄될 라벨의 제목 텍스트)</span></li>
            <li><b>출력 갯수</b>를 설정합니다 <span style={{ color: "#888" }}>(1~100개)</span></li>
            <li><b>라벨 형식</b>을 선택합니다 <span style={{ color: "#888" }}>(폼텍 규격지 종류)</span></li>
            <li><b>[생성]</b> 버튼을 클릭하면 미리보기 QR코드가 생성됩니다</li>
            <li><b>[인쇄]</b> 버튼으로 라벨 형식에 맞게 프린터로 출력합니다</li>
          </ol>

          <hr style={{ margin: "16px 0", borderColor: "#e0e0e0" }} />

          <h3 style={{ margin: "0 0 16px", color: "#1565c0", fontSize: "1.05rem", borderBottom: "2px solid #90caf9", paddingBottom: 8 }}>
            📄 라벨 형식 안내
          </h3>
          <ol style={{ margin: 0, paddingLeft: 20, lineHeight: 2, fontSize: "0.9rem", color: "#333" }}>
            <li>현재 지원 형식: <b>폼텍 21칸 (7×3) LS-3105</b></li>
            <li>A4 용지에 맞춰 자동 배치되며 페이지가 넘어가면 자동으로 다음 페이지에 이어집니다</li>
            <li>각 QR코드는 <b>제목 + 고유 일련번호</b>로 구성됩니다</li>
            <li>개별 QR코드는 미리보기에서 <b>[다운로드]</b>로 PNG 저장 가능합니다</li>
          </ol>

          <hr style={{ margin: "16px 0", borderColor: "#e0e0e0" }} />

          <p style={{ margin: 0, fontSize: "0.82rem", color: "#888", lineHeight: 1.9 }}>
            ➕ <b>[생성]</b> — 입력 조건으로 QR코드 목록 생성 &nbsp;|&nbsp;
            🖨️ <b>[인쇄]</b> — 라벨지 형식으로 인쇄 &nbsp;|&nbsp;
            🔄 <b>[갱신(↺)]</b> — 생성된 QR코드 전체 초기화
          </p>
        </div>
      </div>
    )}
    <Modal onClose={props.onClose} modalSort={"qr"}>
      <Box sx={modalStyles}>
        {/* 헤더 영역 */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            color: "white",
            padding: "0 10px",
            backgroundColor: green[900],
          }}
          className="no-print"
        >
          <CloseIcon sx={closeBtn} onClick={props.onClose} />
          <Typography variant="h6" sx={statusTitle}>
            QR코드 관리
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <button
              onClick={() => setShowHelp(true)}
              style={{
                width: 18, height: 18, borderRadius: "50%",
                border: "1.5px solid rgba(255,255,255,0.7)",
                backgroundColor: "transparent", color: "white",
                fontSize: "0.7rem", fontWeight: "bold",
                cursor: "pointer", lineHeight: 1, padding: 0,
                display: "inline-flex", alignItems: "center", justifyContent: "center",
              }}
              title="사용 방법 안내"
            >?</button>
            <RefreshIcon
              sx={{ color: "yellow", cursor: "pointer", marginTop: "3px" }}
              onClick={resetQRCodes}
            />
          </Box>
        </Box>

        {/* 컨텐츠 영역 */}
        <Box sx={contentStyles} className="no-print">
          {/* 입력 폼 영역 */}
          <Box sx={{ p: 2, backgroundColor: "white" }}>
            <Box sx={inputBox}>
              <TextField
                size="small"
                label="제목"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                sx={{ width: "200px" }}
                required
              />
              <TextField
                size="small"
                label="출력 갯수"
                type="number"
                value={quantity}
                onChange={(e) =>
                  setQuantity(Math.max(1, parseInt(e.target.value) || 1))
                }
                sx={{ width: "100px" }}
                InputProps={{ inputProps: { min: 1, max: 100 } }}
              />
              <FormControl size="small" sx={{ width: "200px" }}>
                <InputLabel>라벨 형식</InputLabel>
                <Select
                  value="LS-3105"
                  onChange={(e) => {
                    // LS-3105만 선택 가능하도록 설정
                    if (e.target.value !== "LS-3105") {
                      return;
                    }
                    setLabelFormat(e.target.value);
                  }}
                  label="라벨 형식"
                >
                  {Object.entries(LABEL_FORMATS)
                    .sort((a, b) => {
                      // 칸수(rows * cols)를 기준으로 내림차순 정렬
                      const aCells = a[1].rows * a[1].cols;
                      const bCells = b[1].rows * b[1].cols;
                      return bCells - aCells;
                    })
                    .map(([key, format]) => (
                      <MenuItem
                        key={key}
                        value={key}
                        sx={{
                          color: key === "LS-3105" ? "inherit" : "grey.500",
                        }}
                      >
                        <Typography variant="body1">{format.name}</Typography>
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <Box sx={buttonGroup}>
                <Button
                  sx={generateBtn}
                  onClick={generateQRCodes}
                  variant="contained"
                  startIcon={<AddBoxIcon />}
                  disabled={!title.trim()}
                >
                  생성
                </Button>
                <Button
                  sx={downloadAllBtn}
                  onClick={printQRCodes}
                  variant="contained"
                  startIcon={<FileDownloadIcon />}
                  disabled={qrCodes.length === 0}
                >
                  인쇄
                </Button>
              </Box>
            </Box>
          </Box>

          <Divider />

          {/* QR 코드 미리보기 영역 */}
          {qrCodes.length > 0 && (
            <Box sx={previewStyles.previewContainer}>
              <Box sx={previewStyles.qrGrid}>
                {qrCodes.map((qrCode) => (
                  <Box key={qrCode.id} sx={previewStyles.qrCell}>
                    <QRCode
                      id={`qr-code-${qrCode.id}`}
                      value={qrCode.value}
                      size={120}
                      level={"H"}
                      includeMargin={false}
                    />
                    <Typography sx={previewStyles.qrValue}>
                      {qrCode.value}
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      sx={previewStyles.downloadButton}
                      onClick={() => downloadSingleQR(qrCode)}
                      startIcon={<FileDownloadIcon />}
                    >
                      다운로드
                    </Button>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
    </>
  );
};

export default QRmanager;
